export const en = {
  meta: {
    homeTitle: "Meridian Motors — Curated Luxury & Performance Vehicles",
    homeDescription:
      "A private showroom for the discerning driver. Hand-selected luxury, performance, and electric vehicles with concierge delivery and bespoke financing.",
    catalogTitle: "Inventory — Meridian Motors",
    catalogDescription:
      "Browse our full inventory of certified luxury, performance, and electric vehicles. Filter by brand, body style, price, mileage and more.",
    notFoundTitle: "Page not found — Meridian Motors",
  },
  nav: {
    home: "Home",
    inventory: "Inventory",
    financing: "Financing",
    about: "About",
    contact: "Contact",
    bookViewing: "Book a viewing",
    toggleMenu: "Toggle menu",
    language: "Language",
  },
  footer: {
    tagline: "Curated luxury automotive",
    blurb:
      "A private showroom for the discerning driver. Every vehicle in our inventory is hand-selected, mechanically certified, and delivered with the attention it deserves.",
    showroom: "Showroom",
    navigate: "Navigate",
    links: ["Inventory", "Financing", "Trade-in", "Warranty", "Concierge", "Press"] as const,
    rights: "All rights reserved.",
    privacy: "Privacy",
    terms: "Terms",
    accessibility: "Accessibility",
    social: "Social link",
  },
  hero: {
    eyebrow: "Summer collection · 2024",
    titleLine1: "The road,",
    titleLine2: "reimagined.",
    copy: "A private showroom of hand-selected luxury and performance vehicles. Concierge delivery, bespoke financing, and an aftersales team that treats your car with the care it deserves.",
    explore: "Explore inventory",
    bookPrivate: "Book a private viewing",
    stats: [
      { k: "12+", v: "Years curating" },
      { k: "340", v: "Cars delivered" },
      { k: "4.9★", v: "Client rating" },
    ] as const,
  },
  featured: {
    eyebrow: "Featured inventory",
    title: "This week's arrivals",
    copy: "A rotating selection of certified pre-owned and factory-new vehicles. Every listing is inspected, documented, and ready to move.",
    viewAll: "View all {count}+ vehicles",
  },
  brands: {
    eyebrow: "Authorized partnerships",
    title: "Brands we represent",
  },
  promotions: {
    eyebrow: "Current promotions",
    title: "Members-only offers",
    items: [
      {
        title: "Spring Concierge",
        body: "Complimentary detail and delivery within 100 miles on all purchases over $80k.",
        cta: "See offer",
      },
      {
        title: "Trade-in Bonus",
        body: "Receive up to $5,000 above appraised value when you trade toward a certified vehicle.",
        cta: "Get appraisal",
      },
      {
        title: "Extended Warranty",
        body: "Two years of concierge maintenance included on every certified pre-owned purchase.",
        cta: "Learn more",
      },
    ] as const,
  },
  financing: {
    eyebrow: "Financing & leasing",
    title: "Ownership on your terms.",
    copy: "Whether it's a purchase, a lease, or a hybrid balloon structure, our in-house finance desk builds a plan tailored to how you actually drive and hold your vehicles.",
    perks: [
      {
        title: "Bespoke rates",
        body: "From 4.9% APR through our private banking partners.",
      },
      {
        title: "Approved in hours",
        body: "Soft-pull pre-approval that won't affect your credit.",
      },
      {
        title: "Flexible terms",
        body: "24 to 84 months, with balloon and lease options.",
      },
    ] as const,
    estimatorTitle: "Payment estimator",
    estimatorHint: "Illustrative only. Final rates depend on credit profile.",
    vehiclePrice: "Vehicle price",
    downPayment: "Down payment",
    term: "Term",
    termUnit: "{n} mo",
    apr: "APR",
    estimatedMonthly: "Estimated monthly",
    getPreApproved: "Get pre-approved",
  },
  testimonials: {
    eyebrow: "Client reviews",
    title: "Trusted by drivers who know.",
    items: [
      {
        name: "Jordan Reyes",
        role: "Founder, Halcyon Studio",
        body: "The buying experience felt more like joining a private club than a dealership. My Vantage was delivered on a Sunday, spotless.",
      },
      {
        name: "Priya Anand",
        role: "Managing Partner",
        body: "Meridian sourced a specific spec of RS7 across two states in under a week. The team's network is unmatched.",
      },
      {
        name: "Marcus Chen",
        role: "Tech Executive",
        body: "I've traded in three cars with them. Every appraisal has been fair and every follow-up has been personal.",
      },
    ] as const,
  },
  about: {
    eyebrow: "Who we are",
    title: "A quieter kind of dealership.",
    p1: "Meridian Motors was founded in 2012 on a simple premise — that buying a performance car should feel as considered as the machine itself. We keep our inventory small, our showroom by appointment, and our team deep in the details.",
    p2: "Every vehicle we sell passes a 220-point mechanical and cosmetic inspection, is documented with a full service history, and is delivered with two years of concierge maintenance included.",
    stats: [
      ["220", "Point inspection"],
      ["48h", "Delivery"],
      ["12yr", "In business"],
    ] as const,
  },
  faq: {
    eyebrow: "FAQ",
    title: "Answers, before you ask.",
    copy: "Common questions from first-time and returning clients. Don't see yours? Our concierge team responds within an hour.",
    items: [
      {
        q: "Do you offer nationwide delivery?",
        a: "Yes. We deliver via fully enclosed, climate-controlled transport anywhere in Venezuela within 5–7 business days.",
      },
      {
        q: "Can I trade in my current vehicle?",
        a: "Absolutely. Book an appraisal and we'll provide a competitive written offer, typically within 24 hours, that you can apply toward any inventory purchase.",
      },
      {
        q: "How does financing work?",
        a: "We work with a network of private banks and captive lenders to build a personalized plan. Soft-pull pre-approval is available and won't affect your credit.",
      },
      {
        q: "Are vehicles inspected before sale?",
        a: "Every vehicle undergoes a 220-point mechanical and cosmetic inspection. Any items outside factory spec are repaired to OEM standard before listing.",
      },
      {
        q: "Do you accept cryptocurrency or wire?",
        a: "We accept certified funds, wire transfers, and select cryptocurrencies through our escrow partner. Speak to your advisor for details.",
      },
    ] as const,
  },
  contact: {
    eyebrow: "Visit the showroom",
    title: "Let's talk about your next car.",
    copy: "Book a private viewing, request a video walkaround, or start a conversation about a specific spec — we source globally.",
    showroom: "Showroom",
    direct: "Direct",
    hours: "Mon–Sat, 9am–7pm",
    concierge: "Concierge",
    mapTitle: "Meridian Motors location map",
    formTitle: "Send an inquiry",
    formHint: "We reply within one business hour, Mon–Sat.",
    fullName: "Full name",
    email: "Email",
    phone: "Phone",
    interestedIn: "Interested in",
    anyVehicle: "Any vehicle",
    message: "Message",
    messagePlaceholder: "Tell us what you're looking for...",
    namePlaceholder: "Alex Morgan",
    emailPlaceholder: "you@company.com",
    phonePlaceholder: "+58 412 000-0000",
    send: "Send inquiry",
    sending: "Sending...",
    sent: "Message received ✓",
    privacyNote: "By submitting you agree to our privacy policy.",
    sendError: "Could not send inquiry",
  },
  catalog: {
    eyebrow: "The collection",
    title: "Inventory",
    subtitleLoaded:
      "{count} vehicles currently in stock. Filter, compare, and reserve online — or book a private viewing at the showroom.",
    subtitleIdle:
      "Filter, compare, and reserve online — or book a private viewing at the showroom.",
    filters: "Filters",
    reset: "Reset",
    searchPlaceholder: "Search model...",
    brand: "Brand",
    bodyStyle: "Body style",
    fuel: "Fuel",
    transmission: "Transmission",
    condition: "Condition",
    maxPrice: "Max price",
    inStockOnly: "In stock only",
    all: "All",
    hideFilters: "Hide filters",
    showFilters: "Filters",
    loading: "Loading…",
    error: "Error",
    results: "{count} results",
    sort: "Sort",
    sorts: {
      featured: "Featured",
      "price-asc": "Price · Low to High",
      "price-desc": "Price · High to Low",
      "year-desc": "Year · Newest",
      "mileage-asc": "Mileage · Lowest",
      "power-desc": "Power · Highest",
    } as const,
    loadError: "Could not load inventory.",
    retry: "Retry",
    emptyInventory: "No vehicles in inventory.",
    emptyInventoryHint: "Check back soon or contact the showroom.",
    noMatch: "No vehicles match those filters.",
    noMatchHint: "Try broadening your search or reset the filters.",
    resetFilters: "Reset filters",
  },
  vehicle: {
    orLease: "or lease",
    reserved: "Reserved",
    inStock: "In stock",
    price: "Price",
    fromMo: "From / mo · 72",
    specifications: "Specifications",
    features: "Features",
    year: "Year",
    body: "Body",
    condition: "Condition",
    mileage: "Mileage",
    power: "Power",
    acceleration: "0-100 km/h",
    topSpeed: "Top Speed",
    drivetrain: "Drivetrain",
    transmission: "Transmission",
    fuel: "Fuel",
    exterior: "Exterior",
    availability: "Availability",
    whatsapp: "WhatsApp",
    call: "Call",
    email: "Email",
    reserveNow: "Reserve now",
    brochure: "Brochure",
    close: "Close",
    prevImage: "Previous image",
    nextImage: "Next image",
  },
  notFound: {
    title: "Page not found",
    copy: "The page you're looking for doesn't exist or has been moved.",
    goHome: "Go home",
  },
  admin: {
    meta: {
      title: "Admin Console — Meridian Motors",
      description: "Internal administration console for Meridian Motors.",
    },
    console: "Admin Console",
    collapseSidebar: "Collapse sidebar",
    menu: "Menu",
    conciergeAi: "Concierge AI",
    conciergeCopy: "Draft listings, reply to leads and auto-tag inventory.",
    enablePreview: "Enable preview",
    searchPlaceholder: "Search vehicles, leads, VIN…",
    toggleTheme: "Toggle theme",
    recentActivity: "Recent activity",
    minutesAgo: "a few minutes ago",
    notifications: [
      "New lead — Elena Kovacs on Audi RS7",
      "Test drive booked — BMW X7 M60i",
      "Polestar 4 listing published",
      "SEO score improved: +6 pts this week",
    ] as const,
    roleSalesDirector: "Sales Director",
    myAccount: "My account",
    profile: "Profile",
    preferences: "Preferences",
    billing: "Billing",
    returnToSite: "Return to site",
    crumbs: {
      admin: "Admin",
      vehicles: "Vehicles",
      leads: "Leads & CRM",
      media: "Media Library",
      seo: "SEO & Analytics",
      analytics: "Inventory Insights",
      settings: "Settings",
      new: "New",
    },
    nav: {
      dashboard: "Dashboard",
      vehicles: "Vehicles",
      leads: "Leads & CRM",
      media: "Media Library",
      seo: "SEO & Analytics",
      analytics: "Inventory Insights",
      settings: "Settings",
    },
    common: {
      somethingWrong: "Something went wrong",
      retry: "Retry",
      noData: "No data available.",
      vsLastMonth: "vs last month",
      previous: "Previous",
      next: "Next",
      more: "More",
      yes: "Yes",
      no: "No",
      justNow: "Just now",
      available: "Available",
      reserved: "Reserved",
      featured: "Featured",
      all: "All",
      edit: "Edit",
      duplicate: "Duplicate",
      feature: "Feature",
      unfeature: "Unfeature",
      unpublish: "Unpublish",
      archive: "Archive",
      reply: "Reply",
    },
    dashboard: {
      title: "Executive dashboard",
      description: "Real-time overview of inventory, engagement and pipeline health.",
      newListing: "New listing",
      totalInventory: "Total inventory",
      availableNow: "Available now",
      reservedHint: "{count} reserved",
      inventoryValue: "Inventory value",
      avgPrice: "Avg. vehicle price",
      totalLeads: "Total leads (30d)",
      unreadInquiries: "Unread inquiries",
      needsTriage: "needs triage",
      conversionRate: "Conversion rate",
      featuredVehicles: "Featured vehicles",
      trafficTitle: "Traffic & lead generation",
      trafficSubtitle: "Daily visits vs qualified inquiries — last 30 days",
      sourcesTitle: "Traffic sources",
      sourcesSubtitle: "Share of sessions",
      revenueTitle: "Revenue by month",
      revenueSubtitle: "Gross sales — trailing 12 months",
      brandMix: "Brand mix",
      brandMixSubtitle: "Inventory by manufacturer",
      funnelTitle: "Sales funnel",
      funnelSubtitle: "30-day journey",
      engagedTitle: "Most engaged vehicles",
      engagedSubtitle: "Views & inquiries",
      leadsCount: "{count} leads",
    },
    vehicles: {
      title: "Vehicle inventory",
      description: "Manage listings, publication status, gallery and financing details.",
      newVehicle: "New vehicle",
      empty: "No vehicles in inventory.",
      search: "Search by brand, model, VIN…",
      brand: "Brand",
      body: "Body",
      status: "Status",
      allBrands: "All brands",
      allBodies: "All bodies",
      allStatuses: "All statuses",
      selected: "{count} selected",
      feature: "Feature",
      unpublish: "Unpublish",
      archive: "Archive",
      archived: "Vehicle archived",
      archivedDesc: "You can restore it from the archive.",
      archiveFail: "Could not archive vehicle",
      duplicated: "Vehicle duplicated",
      duplicateFail: "Could not duplicate vehicle",
      archivedMany: "Vehicles archived",
      archiveManyFail: "Archive failed",
      colVehicle: "Vehicle",
      colStock: "Stock",
      colMileage: "Mileage",
      colPrice: "Price",
      colStatus: "Status",
      colViews: "Views",
      noMatch: "No vehicles match your filters",
      showing: "Showing {filtered} of {total} vehicles",
    },
    leads: {
      title: "Leads & CRM",
      description: "Every inquiry from the storefront in one intelligent pipeline.",
      empty: "No leads yet.",
      table: "table",
      board: "board",
      search: "Search leads by name or email…",
      colLead: "Lead",
      colVehicle: "Vehicle",
      colSource: "Source",
      colStatus: "Status",
      colOwner: "Owner",
      colValue: "Value",
      movedTo: "Lead moved to {status}",
      updateFail: "Could not update lead",
      email: "Email",
      phone: "Phone",
      owner: "Owner",
      created: "Created",
      source: "Source",
      priority: "Priority",
      message: "Message",
      activity: "Activity",
      activityCreated: "Lead created from Contact Form",
      activityAssigned: "Auto-assigned to {owner}",
      activityOpened: "Email opened",
      time2h: "2h ago",
      time1h: "1h ago",
      time45m: "45m ago",
      scheduleTestDrive: "Schedule test drive",
      statuses: {
        New: "New",
        Contacted: "Contacted",
        Qualified: "Qualified",
        "Test Drive": "Test Drive",
        Won: "Won",
        Lost: "Lost",
      },
    },
    media: {
      title: "Media library",
      description: "Every image, video and brochure across the platform — organised and searchable.",
      upload: "Upload assets",
      empty: "No media assets yet.",
      totalAssets: "Total assets",
      storageUsed: "Storage used",
      folders: "Folders",
    },
    seo: {
      title: "SEO & Analytics",
      description: "Metadata health, structured data, keyword rankings and Core Web Vitals.",
      indexedPages: "Indexed pages",
      brokenLinks: "Broken links",
      sitemap: "Sitemap",
      structuredData: "Structured data",
      keywordRankings: "Keyword rankings",
      keyword: "Keyword",
      position: "Position",
      volume: "Volume",
      change: "Change",
      coreWebVitals: "Core Web Vitals",
      valid: "Valid",
      ok: "OK",
    },
    analytics: {
      title: "Inventory insights",
      description: "Deep analytics on inventory composition, turnover and engagement.",
      daysOnMarket: "Days on market",
      bodyStyle: "Body style distribution",
      monthlyAdditions: "Monthly additions",
    },
    settings: {
      title: "Settings",
      description: "Brand identity, contact details, integrations and team access.",
      companyProfile: "Company profile",
      dealershipName: "Dealership name",
      tagline: "Tagline",
      about: "About",
      contact: "Contact",
      phone: "Phone",
      email: "Email",
      address: "Address",
      preferences: "Preferences",
      financingCalc: "Public financing calculator",
      financingCalcDesc: "Show interactive monthly payment estimator.",
      autoAssign: "Auto-assign leads",
      autoAssignDesc: "Round-robin new inquiries to sales team.",
      whatsappTracking: "WhatsApp click tracking",
      whatsappTrackingDesc: "Log WhatsApp CTA taps in analytics.",
      maintenance: "Maintenance mode",
      maintenanceDesc: "Hide storefront and show a coming-soon page.",
      team: "Team",
      adminBadge: "Admin",
      taglineValue: "A private showroom for the discerning driver.",
      aboutValue:
        "Curated luxury, performance and electric vehicles with concierge delivery.",
      teamMembers: [
        "Ava Marchetti — Sales Director",
        "Jonas Weller — Senior Advisor",
        "Priya Ramesh — Concierge",
        "Marcus Vale — Detailing Lead",
      ] as const,
    },
    editor: {
      back: "Back to inventory",
      notFound: "Vehicle not found.",
      newTitle: "New vehicle listing",
      newDescription: "Create a new listing — all fields can be edited later.",
      editDescription: "Update listing details, gallery, pricing and publication status.",
      autofill: "Autofill with AI",
      createListing: "Create listing",
      saveChanges: "Save changes",
      created: "Vehicle created",
      updated: "Vehicle updated",
      savedDesc: "Changes saved to the inventory catalog.",
      saveFail: "Could not save vehicle",
      sections: {
        general: "General",
        pricing: "Pricing",
        specs: "Specifications",
        features: "Features",
        gallery: "Gallery",
        seo: "SEO",
        status: "Publication",
      },
      brand: "Brand",
      model: "Model",
      trim: "Trim",
      year: "Year",
      vin: "VIN",
      stockNumber: "Stock number",
      slug: "Slug",
      exteriorColor: "Exterior color",
      interiorColor: "Interior color",
      description: "Description",
      descriptionPlaceholder:
        "Craft a compelling narrative highlighting design, performance and provenance…",
      listPrice: "List price (USD)",
      promoPrice: "Promotional price (USD)",
      optional: "Optional",
      downPayment: "Down payment %",
      apr: "APR %",
      term: "Term (months)",
      warranty: "Warranty (years)",
      estimatedMonthly: "Estimated monthly",
      perMonth: "/mo",
      body: "Body",
      fuel: "Fuel",
      transmission: "Transmission",
      drivetrain: "Drivetrain",
      horsepower: "Horsepower",
      torque: "Torque (lb-ft)",
      acceleration: "0–100 km/h (s)",
      topSpeed: "Top speed (km/h)",
      mileage: "Mileage (mi)",
      doors: "Doors",
      seats: "Seats",
      condition: "Condition",
      conditionNew: "New",
      conditionCertified: "Certified",
      conditionUsed: "Used",
      addFeaturePlaceholder: "Add a feature — e.g. Bang & Olufsen 3D sound",
      add: "Add",
      noFeatures: "No features yet — start typing above.",
      dropImages: "Drop images or click to upload",
      dropHint: "Up to 20 photos · JPG, PNG, WebP · auto-optimized on upload",
      uploadInfo: "Image upload requires a storage backend to persist media.",
      cover: "Cover",
      seoTitle: "SEO title",
      metaDescription: "Meta description",
      canonical: "Canonical URL",
      keywords: "Keywords",
      searchPreview: "Search preview",
      forSale: "for Sale | Meridian Motors",
      published: "Published",
      publishedDesc: "Listing is publicly visible on the storefront.",
      featuredHome: "Featured on homepage",
      featuredHomeDesc: "Highlighted in curated collections and featured grids.",
      availablePurchase: "Available for purchase",
      availablePurchaseDesc: "Uncheck to reserve or mark as sold pending contract.",
      allowFinancing: "Allow financing calculator",
      allowFinancingDesc: "Show interactive monthly payment estimator on the detail page.",
      publicationStatus: "Publication status",
      status: "Status",
      live: "Live",
      draft: "Draft",
      featuredLabel: "Featured",
      lastEdit: "Last edit",
      savePublish: "Save & publish",
      completeness: "Completeness",
      completenessHint: "86% — add interior photos and warranty terms to reach 100%.",
    },
  },
} as const;
